import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { createContext, useContext, useState, useRef, useEffect, useCallback, } from 'react';
import { styled, useTheme, keyframes } from 'styled-components';
import { useDebouncedCallback } from 'use-debounce';
import { Header } from './header.js';
import { IconButton } from './icon-button.js';
import { noop } from '@navinc/utils';
const moveOffScreen = keyframes `
  from { right: 0%; }
  to   { right: -100%; }
`;
const moveOnScreen = keyframes `
  from { right: -100%; }
  to   { right: 0%; }
`;
const suddenIn = keyframes `
  from { width: 100%; opacity: 0; }
  to   { width: 100%; opacity: 1; }
`;
const suddenOut = keyframes `
  0%    { width: 100%; opacity: 1; }
  99.9% { width: 100%; }
  100%  { width: 0%; opacity: 0; }
`;
const Backdrop = styled.div.withConfig({ displayName: "brc-sc-Backdrop", componentId: "brc-sc-9uhvl5" }) `
  align-items: flex-start;
  animation: 0.2s ${({ theme }) => theme.materialTransitionTiming} both
    ${({ isOpen }) => (isOpen ? suddenIn : suddenOut)};
  background-color: ${({ isOpen }) => (isOpen ? 'hsl(0deg 0% 0% / 20%)' : 'transparent')};
  box-sizing: border-box;
  display: flex;
  height: 100%;
  justify-content: flex-end;
  overflow: hidden;
  pointer-events: ${({ isOpen }) => (isOpen ? 'initial' : 'none')};
  position: fixed;
  right: 0;

  & * {
    box-sizing: border-box;
  }
`;
const drawerHeightVariations = (theme, type, mobileFullPage) => {
    switch (type) {
        case 'dynamic':
            return `
    border-bottom-left-radius: 4px;
    border-top-left-radius: 4px;
    height: ${mobileFullPage ? '100%' : 'auto'} ;
    margin-top: ${mobileFullPage ? '0' : '100px'};
    max-height: ${!mobileFullPage && 'calc(100vh - 100px)'};
    min-height: 80px;
    @media (${theme.forLargerThanPhone}) {
    height: auto;
    max-height: calc(100vh - 100px);
    }
  `;
        case 'full':
            return `
    align-content: flex-start;
    height 100vh;
    height: 100dvh;
  `;
        default:
            return null;
    }
};
const contentHeightVariations = {
    dynamic: 'max-height: calc(100vh - 160px);',
    full: 'max-height: calc(100vh - 65px);',
};
const Content = styled.div.withConfig({ displayName: "brc-sc-Content", componentId: "brc-sc-160un1c" }) `
  overflow: auto;
  grid-column: 1 / -1;
  font-family: ${({ theme }) => theme.fontPrimary};
  ${({ heightVariation }) => { var _a; return (_a = contentHeightVariations[heightVariation]) !== null && _a !== void 0 ? _a : contentHeightVariations.dynamic; }};
`;
const Drawer = styled.div.withConfig({ displayName: "brc-sc-Drawer", componentId: "brc-sc-1qi0dwl" }) `
  animation: 0.2s ${({ theme }) => theme.materialTransitionTiming} both
    ${({ isOpen }) => (isOpen ? moveOnScreen : moveOffScreen)};
  background-color: ${({ theme }) => theme.navNeutralLight};
  display: grid;
  grid-template-columns: min-content 1fr;
  grid-template-rows: ${({ mobileFullPage }) => mobileFullPage && 'auto 1fr'};
  gap: 16px;
  border-right: solid 1px ${({ theme }) => theme.border};
  overflow: hidden;
  padding: 16px;

  /* only use transition when not touching, otherwise dragging feels sluggish */
  ${({ isTouching, theme }) => !isTouching && `transition: transform 0.2s ${theme.materialTransitionTiming};`}
  ${({ theme, heightVariation, mobileFullPage }) => { var _a; return (_a = drawerHeightVariations(theme, heightVariation, mobileFullPage)) !== null && _a !== void 0 ? _a : drawerHeightVariations(theme, 'dynamic'); }}

  width: 100%;

  @media (${({ theme }) => theme.forLargerThanPhone}) {
    position: ${({ orientBottom }) => orientBottom && `absolute`};
    bottom: ${({ orientBottom }) => orientBottom && `0`};
    width: ${({ orientBottom, width }) => width !== null && width !== void 0 ? width : (orientBottom ? `calc(100% - 260px)` : `485px`)};
    padding: 24px 28px 32px;
    max-width: calc(100vw - 55px);
  }
`;
export const InfoDrawerContext = createContext({
    mobileFullPage: false,
    orientBottom: false,
    heightVariation: undefined,
    width: undefined,
    isOpen: false,
    setIsOpen: noop,
    content: null,
    title: null,
    header: null,
    setInfoDrawer: noop,
    closeHandlersRef: { current: [] },
    close: noop,
});
export const InfoDrawerProvider = ({ children }) => {
    const [groupedConfig, setGroupedConfig] = useState({
        mobileFullPage: false,
        orientBottom: false,
        content: null,
        contentProps: undefined,
        drawerProps: undefined,
        title: null,
        header: null,
        heightVariation: undefined,
        width: undefined,
    });
    const [isOpen, setIsOpen] = useState(false);
    const closeHandlersRef = useRef([]);
    const { mobileFullPage, orientBottom, content, contentProps, drawerProps, title, header, heightVariation, width } = groupedConfig;
    const close = useCallback((noClose = false) => {
        if (!noClose) {
            setIsOpen(false);
        }
        closeHandlersRef.current.forEach((closeHandler) => closeHandler());
    }, []);
    const setInfoDrawer = useCallback((config = {}) => {
        setGroupedConfig((currentGroupedConfig) => (Object.assign(Object.assign({}, (config.isOpen ? {} : currentGroupedConfig)), config)));
        setIsOpen((currentIsOpen) => {
            var _a;
            const shouldBeOpen = (_a = config.isOpen) !== null && _a !== void 0 ? _a : currentIsOpen;
            if (currentIsOpen && !shouldBeOpen) {
                close(true);
            }
            return shouldBeOpen;
        });
    }, [close]);
    return (_jsx(InfoDrawerContext.Provider, { value: {
            mobileFullPage,
            orientBottom,
            content,
            contentProps,
            drawerProps,
            isOpen,
            setIsOpen,
            title,
            header,
            closeHandlersRef,
            close,
            setInfoDrawer,
            heightVariation,
            width,
        }, children: children }));
};
export const useInfoDrawer = ({ onClose } = {}) => {
    const { mobileFullPage, orientBottom, content, contentProps, drawerProps, title, header, setInfoDrawer, closeHandlersRef, isOpen, heightVariation, width, } = useContext(InfoDrawerContext);
    if (onClose && !closeHandlersRef.current.includes(onClose)) {
        closeHandlersRef.current = closeHandlersRef.current.concat(onClose);
    }
    // When the component unmounts, we want to clean up our onClose handler.
    useEffect(() => () => {
        closeHandlersRef.current = closeHandlersRef.current.filter((closeHandler) => closeHandler !== onClose);
    }, [closeHandlersRef, onClose]);
    return {
        mobileFullPage,
        orientBottom,
        content,
        contentProps,
        drawerProps,
        title,
        header,
        isOpen,
        setInfoDrawer,
        heightVariation,
        width,
    };
};
const UnstyledInfoDrawer = ({ variation, heightVariation = 'dynamic', className, }) => {
    const theme = useTheme();
    const infoDrawerState = useInfoDrawer();
    const { mobileFullPage, orientBottom, content: ContentComponent, contentProps, drawerProps, title, header: HeaderComponent, isOpen, setInfoDrawer, heightVariation: heightVariationOverride, width, } = infoDrawerState;
    const [moveStartX, setMoveStartX] = useState(0);
    const [translateX, setTranslateX] = useState(0);
    const onTouchMove = useDebouncedCallback((e) => {
        onTouchMove.cancel();
        e.persist();
        setTranslateX(e.touches[0].clientX);
    }, 2, { maxWait: 5 });
    const configuredHeightVariation = heightVariationOverride !== null && heightVariationOverride !== void 0 ? heightVariationOverride : heightVariation;
    return (_jsx(Backdrop, { isOpen: isOpen, className: className, "data-testid": "info-drawer:backdrop", onTouchStart: (e) => setMoveStartX(e.touches[0].clientX), onTouchMove: onTouchMove, onTouchEnd: () => {
            onTouchMove.cancel();
            if (translateX - moveStartX > 180) {
                setInfoDrawer({ isOpen: false });
            }
            setMoveStartX(0);
            setTranslateX(0);
        }, onClick: (e) => {
            e.persist();
            if ('dataset' in e.target && e.target.dataset.testid === 'info-drawer:backdrop') {
                setInfoDrawer({ isOpen: false });
            }
        }, children: _jsxs(Drawer, Object.assign({ mobileFullPage: mobileFullPage, orientBottom: orientBottom, style: {
                transform: `translateX(${isOpen ? Math.max(translateX - moveStartX, 0) : 485}px)`,
            }, isOpen: isOpen, isTouching: moveStartX !== 0, variation: variation, heightVariation: configuredHeightVariation, width: width, onAnimationEnd: () => {
                if (!isOpen) {
                    // Reset content, contentProps and drawerProps to ensure we are not holding onto old state. If the same info drawer was
                    // displayed, the content and any state would have persisted, which may lead to unexpected behavior
                    setInfoDrawer({ content: undefined, contentProps: undefined, drawerProps: undefined });
                }
            }, "data-testid": "info-drawer:drawer" }, drawerProps, { children: [HeaderComponent || (_jsxs(_Fragment, { children: [_jsx(IconButton, { name: "actions/close", onClick: () => setInfoDrawer({ isOpen: false }), variation: "neutral", "data-testid": "info-drawer:close-button", size: "18px", color: theme.navNeutralDark }), _jsx(Header, { size: "sm", children: title })] })), _jsx(Content, { heightVariation: configuredHeightVariation, children: typeof ContentComponent === 'function' ? (_jsx(ContentComponent, Object.assign({}, infoDrawerState, contentProps))) : (ContentComponent) })] })) }));
};
/**
# Info Drawer

A right hand drawer information drawer. This feature consists of the `<InfoDrawer />` component itself -- which will need to be placed once somewhere in your application, an `<InfoDrawerProvider />` component which needs to be rendered higher up in the tree than the `<InfoDrawer />` component and any component that wishes to manage the drawer, and a `useInfoDrawer` hook that allows the consumer to set the drawers `title`, `content`, and `isOpen` state.

## <InfoDrawer />

The UI for the actual information drawer. You will likely want to render this fairly high up in your render tree. The `<InfoDrawer>` component accepts three props: `variation`, `heightVariation`, and `className`

### Props

#### orientBottom

The `orientBottom` prop allows the consumer to have the info drawer on the bottom of the page.  By default the drawer will be on the right side of the page.
#### variation

The `variation` prop allows the consumer to set the type of info drawer being used. This primarily translates to a background color. The options are:

- blue
- default

If the prop is omitted, `default` is used.

#### heightVariation

The `heightVariation` prop switches between a more traditional full height drawer and a drawer who's height is governed by it's content. The options for this prop are:

- full
- dynamic

If the prop is omitted, `dynamic` is used.

#### className

This is the className prop that is passed through to the underlying element.

## <InfoDrawerProvider />

The context provider for the info drawer. This component is necessary to allow other components deeper in the render tree, using the accompanying `useInfoDrawer` hook to control the state of the `<InfoDrawer />` component. It does not accept and props.

## useInfoDrawer

The `useInfoDrawer` hook allows the consumer to configure certain aspects of the drawer and to control it's state. The consumer can pass a configuration object to the hook to initialize it in the context of that component with a `onClose` callback to be called when ever the drawer is closed.

The hook itself returns an object that includes the current state of the drawer, and a setter to update the drawer's state. The updater takes and object that is then shallowly merged with the existing state.

```javascript
const { content, isOpen, setInfoDrawer, title } = useInfoDrawer({ onClose })
```

### Configuration

#### onClose

The `onClose` option regsiters a callback that will be called when ever the drawer is closed either programatically, or by user action. The callback is automatically cleaned up when the consuming component is removed from the render tree (unmounted).

### Return values

#### title

The current value of the title in the info drawer can be read from this value. Be aware the the `title` value may include JSX so there is no guarantee that the value will be a string.

> Note that the header is rendered in a BRC `<Header />` component with its size prop set to `small` (16px).

#### content

The current value of the main content in the info drawer can be read from this value. Be aware the the `content` value may include JSX so there is no guarantee that the value will be a string. If a functional component is provided, the value returned from `useInfoDrawer` will be passed in as props

Static JSX content:
```jsx
{
  content: <div>some content</div>
}
```

Functional component:
```jsx
{
  content: ({ isOpen }) => <div>some content {isOpen ? 'Open' : 'Closed'}</div>
}
```

Functional component using hooks and custom props
```jsx
const ContentComponent = ({ isOpen, customProp }) => {
  const [value, setValue] = useState(0) // hooks can be used as normal
  return (
    <>
      <div>{customProp}</div>
      <div>some content {isOpen ? 'Open' : 'Closed'}</div>
    </>
  )
}

{
  content: ContentComponent,
  contentProps: { customProp: 'Hello world' },
}
```

#### contentProps

These props are passed to `content` if is component function.  See example above.

#### isOpen

The current open state of the info drawer can be read from this value.

#### setInfoDrawer

The values of any of `title`, `content`, or `isOpen` may only be udated using the `setInfoDrawer` function. It is up to the consumer to control the state of the drawer. It should also be noted that any other component in the render tree may also use the `useInfoDrawer` hook to change it's state. The current state of the drawer can always be determined by interogating the values returned from the hook.

 */
export const InfoDrawer = styled(UnstyledInfoDrawer).withConfig({ displayName: "brc-sc-InfoDrawer", componentId: "brc-sc-dcu64d" }) ``;
//# sourceMappingURL=info-drawer.js.map